import { Link } from "react-router-dom";
import { formatDate, formatAddress, formatPhoneNumber } from "../../utils";
import { Badge, Button, Popover } from "../../shared";
import { AppType } from "@/types/config";
import { StatusBadge } from "@/components";
import { useDraggable } from "@dnd-kit/core";
import { ClockIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { hasStoredPermission } from "@/utils/authority";
import { PERMISSIONS } from "@/constants/access-control";
import LocationTableCell from "@/pages/Jobs/Legs/LocationTableCell";

const canEditJobs = () => hasStoredPermission(PERMISSIONS.JOBS_EDIT);
import React from "react";
import { format, formatDistanceToNow } from "date-fns";

// Draggable Row Component
const DraggableTableRow: React.FC<{
  children: React.ReactNode;
  job: any;
  className?: string;
}> = ({ children, job, className }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `unassigned-job-${job._id}`,
      data: {
        type: "unassigned-job",
        job: job,
      },
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={className}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

const getUnassignedJobsTableConfig = (
  appType: AppType,
  options: { mode?: "unassigned" | "assigned"; actionLabel?: string } = {},
) => {
  const isService = appType === "serviceRepair";
  const isTrucking = appType === "trucking";
  const mode = options.mode || "unassigned";
  const actionLabel =
    options.actionLabel || (mode === "assigned" ? "Reassign" : "Assign");

  const COLUMN_DEFINITIONS = [
    // {
    //   accessorKey: "loadNumber",
    //   // filterFn: (row, id, value) => {
    //   //   return value.includes(row.getValue(id));
    //   // },
    //   accessorFn: (e) => e.loadNumber || e.orderNumber,
    //   id: "loadNumber",
    //   visible: true,
    //   required: true,
    //   minWidth: 250,
    //   editable: isAuthorized("owner,admin"),
    //   cell: ({ getValue, row: { original } }) => {
    //     return (
    //       <Link
    //         className="text-primary-600 hover:underline"
    //         to={`/jobs/${original._id}`}
    //       >
    //         {getValue() || original.orderNumber}
    //       </Link>
    //     );
    //   },
    // },
    {
      header: isTrucking ? "Load Number" : "Job ID",
      accessorKey: isTrucking ? "loadNumber" : "jobId",
      id: isTrucking ? "loadNumber" : "jobId",
      cell: ({ row, table }) => {
        // Safely extract job ID and display value
        const jobId =
          typeof row.original.jobId === "object" && row.original.jobId
            ? row.original.jobId._id || row.original.jobId.id
            : row.original.jobId;

        const displayValue = isTrucking
          ? row.original.jobId?.loadNumber || row.original.loadNumber || "N/A"
          : row.original.jobId?.jobId ||
            row.original.jobId?._id ||
            row.original.jobId ||
            "N/A";

        return (
          <div className="flex items-center gap-2">
            <Link
              to={`/jobs/${jobId}`}
              className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()} // Prevent row drag when clicking link
            >
              {displayValue}
            </Link>
            {/* Show grouping indicator if there are multiple legs for this job */}
            {row.original._legCount > 1 && (
              <Badge color="gray" size="sm" className="text-xs font-medium">
                {row.original._legCount} legs
              </Badge>
            )}
          </div>
        );
      },
      size: 150, // Increased size to accommodate the badge
    },
    {
      header: isTrucking ? "Order Number" : "Job ID",
      accessorKey: isTrucking ? "orderNumber" : "jobId",
      id: isTrucking ? "orderNumber" : "jobId",
      cell: ({ row, table }) => {
        // For trucking: jobId is an object, for service: jobId might be a string
        const jobId =
          typeof row.original.jobId === "object" && row.original.jobId
            ? row.original.jobId._id || row.original.jobId.id
            : row.original.jobId;

        const displayValue = isTrucking
          ? row.original.orderNumber
          : typeof row.original.jobId === "object" && row.original.jobId
            ? row.original.jobId.jobId || row.original.jobId._id
            : row.original.jobId;

        return (
          <button
            className="text-primary-600 hover:text-primary-800 hover:underline font-medium cursor-pointer"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row drag when clicking
              // Access the openLegModal handler from table meta
              const openLegModal = table.options.meta?.openLegModal;
              if (openLegModal) {
                openLegModal(row.original);
              }
            }}
          >
            {displayValue}
          </button>
        );
      },
      size: 120,
    },
    // {
    //   header: "Client",
    //   accessorFn: (row: any) => {
    //     return row.customer?.name;
    //   },
    //   id: "client",
    //   cell: ({ row }) => row.original.customer?.name || "N/A",
    //   size: 150,
    // },
    {
      header: isTrucking ? "Origin" : "Service Location",
      id: "origin",
      cell: ({ row }) => {
        if (isTrucking) {
          const leg = row.original;

          // Backend transforms route.origin to route.startLocation for frontend
          const origin = leg?.route?.origin;

          if (!origin) return "N/A";

          // Extract name and address info
          const name = origin.name || "";

          // Format like formatAddress but with name on first line
          if (name || origin.address) {
            if (
              origin.address &&
              typeof origin.address === "object" &&
              origin.address !== null
            ) {
              const { address: street, city, state, zipCode } = origin.address;
              const addressParts = [city, state, zipCode].filter(Boolean);
              const secondLine = addressParts.join(", ");

              return (
                <>
                  {name && <div className="font-medium">{name}</div>}
                  {street && <div>{street}</div>}
                  {secondLine && (
                    <div className="text-sm text-gray-600">{secondLine}</div>
                  )}
                  <div className="flex space-x-1 mt-1">
                    {canEditJobs() && origin.precall && (
                      <Popover
                        className="w-43"
                        position="top"
                        trigger={<Badge>Precall</Badge>}
                        offset={[-12, 0]}
                      >
                        {origin.receivingHours?.open && (
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4" />
                            <span className="text-gray-900 font-semibold text-base mx-2">
                              {origin.receivingHours.open}-
                              {origin.receivingHours.close}
                            </span>
                          </div>
                        )}
                        {origin.phone ? (
                          <div className="flex items-center">
                            <PhoneIcon className="w-4 h-4" />
                            <a
                              href={`tel:${origin.phone}`}
                              className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                            >
                              {formatPhoneNumber(origin.phone)}
                            </a>
                          </div>
                        ) : (
                          "Set # for location"
                        )}
                      </Popover>
                    )}
                    {origin.note && (
                      <Popover
                        className="w-43"
                        position="top"
                        trigger={<Badge color="green">Note</Badge>}
                        offset={[-12, 0]}
                      >
                        {origin.note}
                      </Popover>
                    )}
                  </div>
                </>
              );
            } else {
              // Simple fallback for string addresses or when address is a string
              const addressText =
                typeof origin.address === "string" ? origin.address : "";
              return (
                <>
                  {name && <div className="font-medium">{name}</div>}
                  {addressText && <div className="text-sm">{addressText}</div>}
                </>
              );
            }
          } else {
            return "N/A";
          }
        }
        // Service/Repair: use the same exact cell UI as Job Show legs
        const serviceLocation =
          row.original.serviceLocation ||
          row.original.location ||
          row.original.serviceLocation?.address ||
          null;

        if (!serviceLocation) return "N/A";

        // LocationTableCell expects { name, address, phone, note, precall, ... }
        // If older data shape is "serviceLocation.address" (address object), wrap it.
        const normalized =
          serviceLocation?.address && !serviceLocation?.name
            ? { name: "Service Location", address: serviceLocation.address }
            : serviceLocation;

        return <LocationTableCell data={normalized} />;
      },
      size: 200,
    },
    ...(isTrucking
      ? [
          {
            header: "Destination",
            accessorFn: (row) =>
              row.legs?.[0]?.route?.destination?.address?.fullAddress,
            id: "destination",
            cell: ({ row }) => {
              const leg = row.original;
              const destination = leg?.route?.destination;

              if (!destination) return "N/A";

              // Extract name and format like origin column
              const name = destination.name || "";

              // Format with name on first line
              if (name || destination.address) {
                if (
                  destination.address &&
                  typeof destination.address === "object" &&
                  destination.address !== null
                ) {
                  const {
                    address: street,
                    city,
                    state,
                    zipCode,
                  } = destination.address;
                  const addressParts = [city, state, zipCode].filter(Boolean);
                  const secondLine = addressParts.join(", ");

                  return (
                    <>
                      {name && <div className="font-medium">{name}</div>}
                      {street && <div>{street}</div>}
                      {secondLine && (
                        <div className="text-sm text-gray-600">
                          {secondLine}
                        </div>
                      )}
                      <div className="flex space-x-1 mt-1">
                        {canEditJobs() && destination.precall && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge>Precall</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.receivingHours?.open && (
                              <div className="flex items-center">
                                <ClockIcon className="w-4 h-4" />
                                <span className="text-gray-900 font-semibold text-base mx-2">
                                  {destination.receivingHours.open}-
                                  {destination.receivingHours.close}
                                </span>
                              </div>
                            )}
                            {destination.phone ? (
                              <div className="flex items-center">
                                <PhoneIcon className="w-4 h-4" />
                                <a
                                  href={`tel:${destination.phone}`}
                                  className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                                >
                                  {formatPhoneNumber(destination.phone)}
                                </a>
                              </div>
                            ) : (
                              "Set # for location"
                            )}
                          </Popover>
                        )}
                        {destination.note && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge color="green">Note</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.note}
                          </Popover>
                        )}
                      </div>
                    </>
                  );
                } else {
                  // Simple fallback for string addresses
                  const addressText =
                    typeof destination.address === "string"
                      ? destination.address
                      : "";
                  return (
                    <>
                      {name && <div className="font-medium">{name}</div>}
                      {addressText && (
                        <div className="text-sm">{addressText}</div>
                      )}
                      <div className="flex space-x-1 mt-1">
                        {canEditJobs() && destination.precall && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge>Precall</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.receivingHours?.open && (
                              <div className="flex items-center">
                                <ClockIcon className="w-4 h-4" />
                                <span className="text-gray-900 font-semibold text-base mx-2">
                                  {destination.receivingHours.open}-
                                  {destination.receivingHours.close}
                                </span>
                              </div>
                            )}
                            {destination.phone ? (
                              <div className="flex items-center">
                                <PhoneIcon className="w-4 h-4" />
                                <a
                                  href={`tel:${destination.phone}`}
                                  className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                                >
                                  {formatPhoneNumber(destination.phone)}
                                </a>
                              </div>
                            ) : (
                              "Set # for location"
                            )}
                          </Popover>
                        )}
                        {destination.note && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge color="green">Note</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.note}
                          </Popover>
                        )}
                      </div>
                    </>
                  );
                }
              } else {
                return "N/A";
              }
            },
            size: 200,
          },
        ]
      : []),
    ...(isTrucking && mode === "assigned"
      ? [
          {
            header: "Current Driver",
            id: "driver",
            cell: ({ row }) => row.original?.driver?.fullName || "Unassigned",
            size: 180,
          },
        ]
      : []),
    {
      header: isTrucking ? "Appointment Date" : "Transaction Date",
      accessorKey: isTrucking ? "appointmentDate" : "transactionDate",
      id: "date",
      cell: ({ row }) => {
        const date = isTrucking
          ? row.original.appointmentDate
          : row.original.transactionDate;
        // console.log("date", date);
        return date ? formatDate(date, false, false, true) : "N/A";
      },
      size: 120,
    },
    // {
    //   header: "Status",
    //   accessorKey: "status",
    //   id: "status",
    //   cell: ({ row }) => {
    //     return <StatusBadge status={row.original.status} />;
    //   },
    //   size: 100,
    // },
    // {
    //   header: "Priority",
    //   accessorKey: "priority",
    //   id: "priority",
    //   cell: ({ row }) => (
    //     <Badge
    //       color={
    //         row.original.priority === "high" ||
    //         row.original.priority === "urgent"
    //           ? "destructive"
    //           : row.original.priority === "medium"
    //           ? "warning"
    //           : "secondary"
    //       }
    //     >
    //       {row.original.priority || "low"}
    //     </Badge>
    //   ),
    //   size: 80,
    // },
    ...(isTrucking
      ? [
          {
            header: "ROUTE",
            accessorFn: (row) => row.original?.route?.mileage,
            id: "route",
            cell: ({ row }) => {
              const route = row.original?.route;
              const mileage = route?.mileage;
              const durationSeconds = route?.fullData?.duration;

              // Convert seconds to readable duration
              const formatDuration = (seconds) => {
                if (!seconds) return null;

                const totalMinutes = Math.round(seconds / 60);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;

                if (hours > 0) {
                  return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
                } else {
                  return `${minutes}min`;
                }
              };

              const duration = formatDuration(durationSeconds);

              // Display both mileage and duration
              const parts = [];
              if (mileage) parts.push(`${mileage} mi`);
              if (duration) parts.push(duration);

              return parts.length > 0 ? (
                <div className="text-sm">
                  {parts.map((part, index) => (
                    <div key={index}>{part}</div>
                  ))}
                </div>
              ) : (
                "N/A"
              );
            },
            size: 120,
          },
        ]
      : []),

    {
      id: "actions",
      header: "Actions",
      cell: ({ row, table }) => (
        // console.log("row", row),
        <Button
          size="xs"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row drag when clicking button
            table.options.meta?.assignResources(row.original);
          }}
        >
          {actionLabel}
        </Button>
      ),
    },
  ];

  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  return {
    COLUMN_DEFINITIONS,
    PAGE_SIZE_OPTIONS,
    DraggableTableRow: mode === "unassigned" ? DraggableTableRow : null,
  };
};

export default getUnassignedJobsTableConfig;
